/**
 * DataStore Module for Custom Functions
 * Replaces: DataStore.cs
 * Original: DataStore.cs - Core cache for IAD/IBD function results
 * 
 * This is a singleton shared state used by custom functions
 * to queue requests and cache results from background API calls.
 */

/**
 * Emit request Keys in the format the backend Key contract specifies:
 *   TICKER | FISCALPERIOD | LINECODE | BROKER | UPDATEDATE | DATAPOINT | ATTRIBUTE
 *
 * Two slots were previously off-contract and are corrected when this is true:
 *   - UPDATEDATE (pos 5) is yyyy-MM-dd, not MM-dd-yyyy  (buildKey, below)
 *   - Calendarized (CY-) keys put the broker in pos 4 and the date in pos 5,
 *     instead of putting the date in pos 4  (buildIADKey in IAD.ts)
 *
 * Set to false to restore the previous behaviour in one place if the backend
 * turns out to still expect the older COM-era shape. NOTE: changing this changes
 * cache keys, so the first load after a flip is a cold cache by design.
 */
export const USE_API_DOC_KEY_FORMAT = false;

// ============================================================================
// Data Store Types
// ============================================================================

export interface CacheEntry {
  value: any;
  timestamp: number;
  expiresAt: number;
}

export interface DataStoreState {
  // Final cached values: Map<fullKey, value>
  // Equivalent to: ConcurrentDictionary<string, object> Cache in .NET
  cache: Map<string, CacheEntry>;

  // Keys waiting to be sent to API: Set<fullKey>
  // Equivalent to: ConcurrentDictionary<string, byte> PendingKeys in .NET
  pendingKeys: Set<string>;

  // Keys currently being fetched: Set<fullKey>
  // Equivalent to: ConcurrentDictionary<string, byte> InFlightKeys in .NET
  inFlightKeys: Set<string>;

  // Timestamp of last successful cache update
  // Equivalent to: long LastCacheUpdateUtcTicks in .NET
  lastCacheUpdateUtcTicks: number;

  // Track if any errors detected
  errorDetected: boolean;
}

// ============================================================================
// Global Data Store (Singleton)
// ============================================================================

/**
 * Global data store instance
 * 
 * IMPORTANT: This is a module-level singleton that persists across
 * custom function invocations. The Office.js custom functions runtime
 * maintains module state between calls.
 */
/**
 * Pinned to globalThis, NOT a plain module const.
 *
 * webpack builds taskpane/commands/functions as separate entry bundles with no
 * shared chunks, and taskpane.html loads all of them into one page. This module is
 * therefore duplicated per bundle, so a plain module-level const would give the
 * task pane and the custom-functions runtime a cache each. That silently breaks
 * every cross-bundle use: DataStore.set() from a pane (the InsertData and
 * BrokerRevisions prefetches) would populate a map IAD_OFJ never reads, and
 * DataStore.clearAll() from Refresh/logout would clear a map IAD_OFJ never reads.
 * Sharing one store through globalThis is what makes those actually reach the
 * custom functions.
 */
const GLOBAL_STORE_KEY = '__INSYNC_IAD_DATASTORE__';

const globalStore: DataStoreState =
  (globalThis as any)[GLOBAL_STORE_KEY] ||
  ((globalThis as any)[GLOBAL_STORE_KEY] = {
    cache: new Map(),
    pendingKeys: new Set(),
    inFlightKeys: new Set(),
    lastCacheUpdateUtcTicks: 0,
    errorDetected: false
  } as DataStoreState);

// ============================================================================
// Cache Persistence
// ============================================================================

/**
 * Every ribbon button opens its own pane URL (taskpane.html?pane=...), so Office
 * navigates the task pane on each switch, which restarts the shared runtime and
 * takes the in-memory cache above with it — leaving every IAD_OFJ to refetch from
 * the network. Mirror the cache into localStorage, the one store both the task
 * pane and the custom-functions runtime can see, so a reload rehydrates instead.
 *
 * Only `cache` is mirrored. pendingKeys/inFlightKeys describe requests belonging
 * to a runtime that no longer exists; restoring them would leave keys looking
 * permanently in-flight and they'd never be fetched again.
 *
 * Entries keep their original expiresAt, so this never widens the staleness
 * window that DEFAULT_TTL_MS already allows.
 */
const PERSIST_KEY = 'INSYNC_IAD_CACHE_V1';
const PERSIST_DEBOUNCE_MS = 1000;
const PERSIST_MAX_BYTES = 4 * 1024 * 1024; // localStorage caps around 5MB per origin

let persistEnabled = typeof localStorage !== 'undefined';
let persistTimer: ReturnType<typeof setTimeout> | null = null;

/** Live (unexpired) entries, newest first so a trim keeps the freshest. */
function collectLiveEntries(): [string, CacheEntry][] {
  const now = Date.now();
  const entries: [string, CacheEntry][] = [];
  globalStore.cache.forEach((entry, key) => {
    if (entry.expiresAt > now) entries.push([key, entry]);
  });
  return entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
}

/**
 * Write the mirror now. Never throws: a failed mirror costs a refetch, but it must
 * never break a formula. On quota failure, retry once with the newest half, then
 * give up for this runtime.
 */
function persistNow(): void {
  if (persistTimer !== null) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  if (!persistEnabled) return;

  let entries = collectLiveEntries();
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const payload = JSON.stringify(entries);
      if (payload.length > PERSIST_MAX_BYTES) {
        entries = entries.slice(0, Math.floor(entries.length / 2));
        continue;
      }
      localStorage.setItem(PERSIST_KEY, payload);
      return;
    } catch {
      entries = entries.slice(0, Math.floor(entries.length / 2));
    }
  }

  persistEnabled = false;
  try { localStorage.removeItem(PERSIST_KEY); } catch { /* best-effort */ }
}

/**
 * Debounced so a bulk load (a revision sheet queues thousands of keys) serializes
 * once after it settles rather than on every set.
 */
function schedulePersist(): void {
  if (!persistEnabled) return;
  if (persistTimer !== null) clearTimeout(persistTimer);
  persistTimer = setTimeout(persistNow, PERSIST_DEBOUNCE_MS);
}

function hydrateFromStorage(): void {
  if (!persistEnabled) return;
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const now = Date.now();
    const entries = JSON.parse(raw) as [string, CacheEntry][];
    for (const [key, entry] of entries) {
      if (entry && typeof entry.expiresAt === 'number' && entry.expiresAt > now) {
        globalStore.cache.set(key, entry);
      }
    }
  } catch {
    // Corrupt or unreadable mirror: start cold rather than fail to load.
    try { localStorage.removeItem(PERSIST_KEY); } catch { /* best-effort */ }
  }
}

// Guarded on globalThis for the same reason as the store: this module exists once
// per bundle, and hydrating or attaching flush listeners per copy would duplicate
// both for no benefit.
const PERSIST_INIT_KEY = '__INSYNC_IAD_DATASTORE_PERSIST_INIT__';

if (persistEnabled && !(globalThis as any)[PERSIST_INIT_KEY]) {
  (globalThis as any)[PERSIST_INIT_KEY] = true;
  hydrateFromStorage();
  // The debounce may not fire before Office navigates the pane away, so flush on
  // the way out. Both events are best-effort, neither is guaranteed alone.
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', persistNow);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') persistNow();
    });
  }
}

// ============================================================================
// Data Store API
// ============================================================================

export class DataStore {
  // Default TTL for cached values (5 minutes)
  static DEFAULT_TTL_MS = 30 * 60 * 1000;

  /**
   * Get the global store instance
   * Equivalent to: DataStore.Cache, DataStore.PendingKeys in .NET
   */
  static getStore(): DataStoreState {
    return globalStore;
  }

  /**
   * Build full cache key from IAD parameters
   * Equivalent to: BuildFullKey() in InSyncAddinMethod.cs
   *
   * Seven pipe-separated fields, matching the request-Key contract:
   *   TICKER | FISCALPERIOD | LINECODE | BROKER | UPDATEDATE | DATAPOINT | ATTRIBUTE
   *
   * The `dp` argument feeds positions 5 and 6 and is split by type:
   *   Pos5 (UPDATEDATE) = dp when it parses as a date, formatted yyyy-MM-dd
   *   Pos6 (DATAPOINT)  = dp when it is not a date (e.g. "E", "A")
   */
  static buildKey(
    ticker: string,
    period?: string,
    ltCode?: string,
    ltType?: string,
    dp?: any,
    attribute?: string,
    keepDateFormat?: boolean
  ): string {
    let pos5 = '';
    let pos6 = '';
    const dpValue = this.normalizeCellValue(dp);

    // Detect if DP is a date (matching C# IsDate logic)
    if (dpValue && this.isDate(dpValue)) {
      const d = this.parseDate(dpValue);
      if (keepDateFormat) {
        pos5 = dpValue;
      } else if (d && !isNaN(d.getTime())) {
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const year = d.getFullYear();
        // UPDATEDATE must be yyyy-MM-dd per the request-Key contract. This slot
        // previously emitted MM-dd-yyyy, so a date-filtered request did not match
        // the historical snapshot the user asked for — it silently fell back to
        // latest. Flip USE_API_DOC_KEY_FORMAT to false to restore MM-dd-yyyy.
        pos5 = USE_API_DOC_KEY_FORMAT
          ? `${year}-${month}-${day}`
          : `${month}-${day}-${year}`;
      }
      pos6 = '';
    } else if (dpValue) {
      pos5 = '';
      pos6 = dpValue;
    }

    const parts = [
      this.normalizeCellValue(ticker).toUpperCase(),
      this.normalizeCellValue(period).toUpperCase(),
      this.normalizeCellValue(ltCode).toUpperCase(),
      this.normalizeCellValue(ltType).toUpperCase(),
      pos5.toUpperCase(),
      pos6.toUpperCase(),
      this.normalizeCellValue(attribute).toUpperCase()
    ];

    return parts.join('|');
  }

  /**
   * Check if value is a date string
   * Equivalent to: IsDate() in InSyncAddinMethod.cs
   */
  private static normalizeCellValue(value: any): string {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return this.formatSlashDate(value);
    const serial = this.excelDateSerial(value);
    if (serial !== null) return this.formatSlashDate(this.excelSerialToDate(serial));
    return String(value).trim();
  }

  /**
   * The serial behind a date cell, or null if this isn't one. Mirrors excelDateSerial
   * in IAD.ts — both build the same keys, so they have to agree. A date cell reaches
   * a custom function as a serial, and a `string`-typed parameter arrives already
   * stringified ("46082"), which would otherwise fail the date test and be keyed as
   * literal text instead of as a date.
   *
   * Window ~1954-2119: wide enough for any fiscal date, clear of small datapoint
   * integers like revision numbers.
   */
  private static excelDateSerial(value: any): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) && value > 20000 && value < 80000 ? value : null;
    }
    const text = String(value ?? '').trim();
    if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
    const serial = Number(text);
    return Number.isFinite(serial) && serial > 20000 && serial < 80000 ? serial : null;
  }

  private static isDate(value: string): boolean {
    if (!value || value.trim() === '') return false;
    return this.parseDate(value) !== null;
  }

  private static parseDate(value: string): Date | null {
    const trimmed = value.trim();
    if (/^[+-]?\d+(?:\.\d+)?$/.test(trimmed)) {
      return null;
    }

    const slashOrDashDate = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:\s.*)?$/;
    const match = trimmed.match(slashOrDashDate);
    if (match) {
      const year = Number(match[3].length === 2 ? `20${match[3]}` : match[3]);
      const month = Number(match[1]) - 1;
      const day = Number(match[2]);
      const date = new Date(year, month, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
    if (iso) {
      const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    // Only fall back to the lenient JS Date parser for strings containing a
    // month name. Otherwise V8 coerces arbitrary text (e.g. "Convert 1" ->
    // 1/1/2001), diverging from the COM add-in's DateTime.TryParse, which
    // rejects it. This keeps cache keys aligned with the COM source of truth.
    if (!/\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(trimmed)) {
      return null;
    }

    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  /**
   * Serial -> local midnight of that calendar date. The serial is a UTC-based day
   * count, so it must be read back with UTC getters and rebased to local; formatting
   * the raw instant with local getters (formatSlashDate) would shift the date back a
   * day for anyone behind UTC.
   */
  private static excelSerialToDate(serial: number): Date {
    const utc = new Date(Math.floor(serial - 25569) * 86400 * 1000);
    return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
  }

  private static formatSlashDate(date: Date): string {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  /**
   * Get value from cache
   * Returns undefined if not found or expired
   */
  static get(key: string): any | undefined {
    const entry = globalStore.cache.get(key);
    
    if (!entry) {
      return undefined;
    }

    // Check expiration
    if (Date.now() > entry.expiresAt) {
      globalStore.cache.delete(key);
      return undefined;
    }

    return entry.value;
  }

  /**
   * Set value in cache
   */
  static set(key: string, value: any, ttlMs?: number): void {
    const now = Date.now();
    globalStore.cache.set(key, {
      value,
      timestamp: now,
      expiresAt: now + (ttlMs || this.DEFAULT_TTL_MS)
    });
    globalStore.lastCacheUpdateUtcTicks = now;
    schedulePersist();
  }

  /**
   * Check if key is pending (waiting for API)
   */
  static isPending(key: string): boolean {
    return globalStore.pendingKeys.has(key);
  }

  /**
   * Check if key is in-flight (being fetched)
   */
  static isInFlight(key: string): boolean {
    return globalStore.inFlightKeys.has(key);
  }

  /**
   * Add key to pending queue
   * Returns true if added, false if already pending or in-flight
   */
  static addPending(key: string): boolean {
    if (globalStore.pendingKeys.has(key) || globalStore.inFlightKeys.has(key)) {
      return false;
    }
    globalStore.pendingKeys.add(key);
    return true;
  }

  /**
   * Move pending keys to in-flight
   * Returns array of keys that were moved
   */
  static movePendingToInFlight(maxKeys?: number): string[] {
    const keys = Array.from(globalStore.pendingKeys);
    const keysToMove = maxKeys ? keys.slice(0, maxKeys) : keys;

    for (const key of keysToMove) {
      globalStore.pendingKeys.delete(key);
      globalStore.inFlightKeys.add(key);
    }

    return keysToMove;
  }

  /**
   * Remove key from in-flight
   */
  static removeInFlight(key: string): void {
    globalStore.inFlightKeys.delete(key);
  }

  /**
   * Mark error detected
   */
  static setErrorDetected(): void {
    globalStore.errorDetected = true;
  }

  /**
   * Check if error was detected
   */
  static hasError(): boolean {
    return globalStore.errorDetected;
  }

  /**
   * Clear all data
   * Equivalent to: DataStore.ClearAll() in .NET
   */
  static clearAll(): void {
    globalStore.cache.clear();
    globalStore.pendingKeys.clear();
    globalStore.inFlightKeys.clear();
    globalStore.lastCacheUpdateUtcTicks = 0;
    globalStore.errorDetected = false;

    // Drop the mirror too, or a reload would resurrect what the caller just
    // cleared (Refresh and logout both rely on this actually clearing).
    if (persistTimer !== null) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    try { localStorage.removeItem(PERSIST_KEY); } catch { /* best-effort */ }
  }

  /**
   * Get cache statistics
   */
  static getStats(): {
    cacheSize: number;
    pendingCount: number;
    inFlightCount: number;
    lastUpdate: number;
    errorDetected: boolean;
  } {
    return {
      cacheSize: globalStore.cache.size,
      pendingCount: globalStore.pendingKeys.size,
      inFlightCount: globalStore.inFlightKeys.size,
      lastUpdate: globalStore.lastCacheUpdateUtcTicks,
      errorDetected: globalStore.errorDetected
    };
  }

  /**
   * Clean up expired entries
   */
  static cleanup(): void {
    const now = Date.now();
    const entriesToDelete: string[] = [];
    
    globalStore.cache.forEach((entry, key) => {
      if (now > entry.expiresAt) {
        entriesToDelete.push(key);
      }
    });
    
    for (const key of entriesToDelete) {
      globalStore.cache.delete(key);
    }
  }
}

// ============================================================================
// Export global store for direct access
// ============================================================================

export { globalStore };

export default DataStore;
